import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Search,
  Print,
  FileDownload,
  Save,
  MoreVert,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";

export default function AttendanceFilters({
  selectedDate,
  onDateChange,
  searchTerm,
  onSearchChange,
  resultCount,
  totalCount,
  onSaveAll,
  onPrint,
  onExport,
  actionsDisabled = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const actionMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={menuOpen}
      onClose={() => setMenuAnchor(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      /* Portaled menu + pickers can leave the anchor focused while #root is aria-hidden (a11y warning). */
      disablePortal={isMobile}
    >
      <MenuItem
        disabled={actionsDisabled}
        onClick={() => {
          setMenuAnchor(null);
          onSaveAll();
        }}
      >
        <ListItemIcon>
          <Save fontSize="small" />
        </ListItemIcon>
        <ListItemText>Save all</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setMenuAnchor(null);
          onPrint();
        }}
      >
        <ListItemIcon>
          <Print fontSize="small" />
        </ListItemIcon>
        <ListItemText>Print</ListItemText>
      </MenuItem>
      <MenuItem
        disabled={actionsDisabled}
        onClick={() => {
          setMenuAnchor(null);
          onExport();
        }}
      >
        <ListItemIcon>
          <FileDownload fontSize="small" />
        </ListItemIcon>
        <ListItemText>Export CSV</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <DatePicker
          views={["month", "year"]}
          openTo="month"
          label="Month & year"
          value={selectedDate}
          onChange={onDateChange}
          closeOnSelect
          slotProps={{
            textField: {
              size: "small",
              sx: { minWidth: { xs: "100%", sm: 200 } },
            },
            mobile: {
              closeOnSelect: true,
            },
          }}
        />
        <TextField
          label="Search tag or alias"
          placeholder="Type to filter…"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          fullWidth
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 240px" }, minWidth: 0 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {resultCount} of {totalCount} animals
        </Typography>
        {isMobile ? (
          <>
            <IconButton
              color="primary"
              aria-label="More actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            {actionMenu}
          </>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={onSaveAll}
              disabled={actionsDisabled}
            >
              Save all
            </Button>
            <Button variant="outlined" startIcon={<Print />} onClick={onPrint}>
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={onExport}
              disabled={actionsDisabled}
            >
              Export
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
